import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Loader2, Search, Crown, Key, Sparkles } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

/**
 * Modal for attaching a loyalty membership to a PAST revenue (<3 days old).
 * Smart-suggestions: the 3 closest loyalty memberships to the revenue timestamp
 * are surfaced as one-click chips. Override-code allows bypassing the 3-day cap
 * for older memberships.
 */
const AttachLoyaltyDialog = ({ open, onOpenChange, revenue, onSuccess }) => {
  const [num, setNum] = useState('');
  const [preview, setPreview] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [overrideCode, setOverrideCode] = useState('');
  const [showOverride, setShowOverride] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const reset = () => {
    setNum('');
    setPreview(null);
    setLookingUp(false);
    setSubmitting(false);
    setOverrideCode('');
    setShowOverride(false);
    setSuggestions([]);
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  // Load suggested closest memberships whenever the dialog opens
  useEffect(() => {
    if (!open || !revenue?.id) return;
    setLoadingSuggestions(true);
    api
      .get(`/loyalty/revenues/${revenue.id}/suggested-memberships?days_window=7&limit=3`)
      .then((res) => setSuggestions(res.data?.items || []))
      .catch(() => setSuggestions([]))
      .finally(() => setLoadingSuggestions(false));
  }, [open, revenue?.id]);

  const lookup = async (membershipNumber) => {
    const value = (membershipNumber || num || '').trim();
    if (!value) return;
    setNum(value);
    setLookingUp(true);
    try {
      const res = await api.get(`/loyalty/lookup-by-membership/${encodeURIComponent(value)}`);
      setPreview(res.data);
    } catch (err) {
      setPreview(null);
      toast.error(err.response?.data?.detail || 'العضوية غير موجودة');
    } finally {
      setLookingUp(false);
    }
  };

  const submit = async () => {
    if (!preview || !revenue) return;
    setSubmitting(true);
    try {
      const payload = { membership_number: preview.membership_number };
      if (overrideCode.trim()) {
        payload.override_code = overrideCode.trim().toUpperCase();
      }
      const res = await api.post(`/loyalty/revenues/${revenue.id}/attach-loyalty`, payload);
      toast.success(
        `⏳ تم تسجيل الطلب — ${res.data?.points_earned || 0} نقطة بانتظار اعتماد المدير (خلال 7 أيام)`
      );
      handleClose(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      const detail = err.response?.data?.detail || 'فشل ربط البطاقة';
      toast.error(detail);
      if (/يوم|day/i.test(detail) && !showOverride) {
        setShowOverride(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const cardOlder = preview && preview.days_since_join >= 3;
  // The backend tries the user's quota silently first; only when quota is 0/exhausted
  // does it require a one-time code. So we don't block submission on cardOlder alone.
  const submitDisabled = !preview || submitting;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-card border-white/10" data-testid="attach-loyalty-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-amber-500" />
            ربط بطاقة ولاء بإيراد سابق
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {revenue && (
            <div className="bg-secondary/30 p-3 rounded-lg text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">الإيراد:</span>
                <span className="font-bold text-primary">{Number(revenue.amount).toFixed(2)} د.إ</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">التاريخ:</span>
                <span>{(revenue.date || '').substring(0, 10)}</span>
              </div>
            </div>
          )}

          {/* Smart suggestions: 3 closest memberships by time delta */}
          <div className="rounded-lg border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5 p-3" data-testid="suggested-memberships">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-semibold text-amber-300">اقتراحات ذكية — أقرب 3 بطاقات بتوقيت الإيراد</span>
            </div>
            {loadingSuggestions ? (
              <div className="text-xs text-muted-foreground text-center py-3">
                <Loader2 className="w-4 h-4 animate-spin inline me-1" />
                جاري البحث...
              </div>
            ) : suggestions.length === 0 ? (
              <p className="text-xs text-muted-foreground py-1">
                لا توجد بطاقات أُنشئت خلال 7 أيام من تاريخ هذا الإيراد.
              </p>
            ) : (
              <div className="space-y-1.5">
                {suggestions.map((s, idx) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => lookup(s.membership_number)}
                    className={`w-full text-start flex items-center justify-between gap-2 p-2 rounded-lg border transition-colors hover:bg-amber-500/15 ${
                      idx === 0
                        ? 'border-amber-500/60 bg-amber-500/10'
                        : 'border-white/10 bg-secondary/30'
                    }`}
                    data-testid={`suggested-${s.membership_number}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-sm flex items-center gap-1.5">
                        {idx === 0 && <span className="text-amber-400 text-[10px]">⭐ الأقرب</span>}
                        <span className="font-mono text-amber-300">#{s.membership_number}</span>
                        <span className="truncate">{s.name}</span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap gap-2">
                        <span>{s.delta_human}</span>
                        {s.phone && <span dir="ltr" className="font-mono">{s.phone}</span>}
                        {s.status === 'pending' && (
                          <span className="text-amber-400">⏳ بانتظار اعتماد المدير</span>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-amber-400 font-bold flex-shrink-0">اختر</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-xs text-yellow-300">
            <p>• الإيراد و بطاقة الولاء يجب أن يكون كلاهما أقل من 3 أيام.</p>
            <p>• النقاط لن تُضاف فوراً — تحتاج اعتماد المدير خلال 7 أيام.</p>
            <p>• <strong>للحالات الأقدم:</strong> سيستخدم النظام تلقائياً حصتك من الأذونات (إن وُجدت)، أو استخدم رمز إذن من المدير.</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">رقم العضوية</label>
            <div className="flex gap-2">
              <Input
                value={num}
                onChange={(e) => setNum(e.target.value)}
                placeholder="مثال: 100001"
                className="bg-secondary/50 border-white/10"
                data-testid="attach-loyalty-membership-input"
                onKeyDown={(e) => e.key === 'Enter' && lookup()}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => lookup()}
                disabled={lookingUp || !num}
                className="border-primary/40"
                data-testid="attach-loyalty-lookup-btn"
              >
                {lookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {preview && (
            <div
              className="p-3 rounded-lg border space-y-1 text-sm"
              style={{
                background: `linear-gradient(135deg, ${preview.level?.color || '#fbbf24'}15, transparent)`,
                borderColor: `${preview.level?.color || '#fbbf24'}55`,
              }}
              data-testid="attach-loyalty-preview"
            >
              <div className="flex justify-between">
                <span className="text-muted-foreground">العضو:</span>
                <span className="font-bold">{preview.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">رقم العضوية:</span>
                <span className="font-mono font-bold">#{preview.membership_number}</span>
              </div>
              {preview.phone && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">الجوال:</span>
                  <span className="font-mono" dir="ltr">{preview.phone}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-white/10 pt-1 mt-2">
                <span className="text-muted-foreground">عمر البطاقة:</span>
                <span className={`font-bold ${cardOlder ? 'text-amber-400' : 'text-emerald-400'}`}>
                  {preview.days_since_join ?? 0} يوم
                </span>
              </div>
              <div className="flex justify-between text-emerald-500 font-bold border-t border-white/10 pt-1 mt-1">
                <span>النقاط التي ستُضاف بعد الاعتماد:</span>
                <span>+{Math.floor(revenue?.amount || 0)} نقطة</span>
              </div>
              {cardOlder && (
                <p className="text-amber-300 text-xs mt-2">
                  ℹ️ بطاقة عمرها أكثر من 3 أيام — سيُحسم إذن من رصيدك تلقائياً. إن نفد الرصيد ستحتاج رمز إذن من المدير.
                </p>
              )}
            </div>
          )}

          {(showOverride || cardOlder) && (
            <div className="border border-amber-500/40 bg-amber-500/5 rounded-lg p-3 space-y-2" data-testid="override-code-section">
              <label className="flex items-center gap-2 text-sm font-medium text-amber-300">
                <Key className="w-4 h-4" />
                رمز الإذن من المدير
              </label>
              <Input
                value={overrideCode}
                onChange={(e) => setOverrideCode(e.target.value.toUpperCase())}
                placeholder="ABC123"
                className="bg-secondary/50 border-amber-500/40 font-mono tracking-widest uppercase"
                maxLength={6}
                data-testid="attach-loyalty-override-input"
              />
              <p className="text-[11px] text-muted-foreground">
                اطلب من المدير توليد رمز من تبويب "رموز الإذن" في إدارة الولاء، ساري لمدة قصيرة ولمرة واحدة.
              </p>
            </div>
          )}

          {!showOverride && !cardOlder && (
            <button
              type="button"
              className="text-xs text-amber-400 hover:underline"
              onClick={() => setShowOverride(true)}
              data-testid="show-override-btn"
            >
              لدي رمز إذن من المدير
            </button>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              onClick={submit}
              disabled={submitDisabled}
              className="flex-1 bg-amber-600 hover:bg-amber-700"
              data-testid="attach-loyalty-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'إرسال للاعتماد'}
            </Button>
            <Button variant="outline" onClick={() => handleClose(false)}>
              إلغاء
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AttachLoyaltyDialog;
